import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'

const EmbeddingGuide = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user, business } = useSelector((state) => state.auth)

  const [isSigningOut, setIsSigningOut] = useState(false)

  const handleSignOut = async () => {
    setIsSigningOut(true)
    
    try {
      await dispatch(signOut()).unwrap()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setTimeout(() => {
        navigate('/signin')
        setIsSigningOut(false)
      }, 500)
    }
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerRightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerIconStyle = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: colors.text.secondary
  }

  const notificationIconStyle = {
    ...headerIconStyle,
    position: 'relative'
  }

  const notificationDotStyle = {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ef4444',
    borderRadius: '50%'
  }

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0,
    marginBottom: '24px'
  }

  const cardStyle = {
    backgroundColor: colors.background.primary,
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '24px'
  }

  const cardTitleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: colors.text.primary,
    margin: '0 0 16px 0'
  }

  const codeBlockStyle = {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '16px',
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: '14px',
    lineHeight: '1.5',
    overflow: 'auto',
    marginBottom: '16px',
    color: '#495057'
  }

  const highlightStyle = {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: '2px 4px',
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: '13px',
    color: '#856404'
  }

  const paragraphStyle = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: colors.text.primary,
    marginBottom: '16px'
  }

  const listStyle = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: colors.text.primary,
    marginBottom: '16px',
    paddingLeft: '20px'
  }

  const listItemStyle = {
    marginBottom: '8px'
  }

  const stepStyle = {
    backgroundColor: colors.background.secondary,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '16px'
  }

  const stepNumberStyle = {
    display: 'inline-block',
    backgroundColor: colors.primary,
    color: 'white',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    textAlign: 'center',
    lineHeight: '24px',
    fontSize: '14px',
    fontWeight: '600',
    marginRight: '12px'
  }

  const copyButtonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '12px',
    transition: 'background-color 0.2s ease'
  }

  const loaderOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
  }

  const loaderContainerStyle = {
    backgroundColor: colors.background.primary,
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    minWidth: '200px'
  }

  const spinnerStyle = {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px auto'
  }

  const loaderTextStyle = {
    fontSize: '16px',
    color: colors.text.primary,
    margin: 0,
    fontWeight: '500'
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Code copied to clipboard!')
    }).catch(err => {
      console.error('Failed to copy: ', err)
    })
  }

  const chatbotScript = `<script src="${process.env.REACT_APP_WIDGET_URL}/chat.js?v=${Date.now()}" data-client-id="${business?.client_id || 'YOUR_CLIENT_ID'}" data-greeting="Hi! I'm your ${business?.name} assistant. How can I help you today? 🚀"></script>`
  
  // Debug: Log the business data to ensure we have the client_id
  console.log('Business data:', business)
  console.log('Client ID:', business?.client_id)

  console.log('EmbeddingGuide rendering, isSigningOut:', isSigningOut)

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="Embedding Guide"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => {}}
          onSignOut={handleSignOut}
        />

        {/* Chatbot Script Card */}
        <div style={cardStyle}>
          <h2 style={cardTitleStyle}>Your KBI Chatbot Script</h2>
          <p style={paragraphStyle}>
            Copy and paste this script into your website's HTML to embed your KBI chatbot. 
            The script includes your unique chatbot ID: <span style={highlightStyle}>{business?.client_id || 'YOUR_CLIENT_ID'}</span>
          </p>
          
          {!business?.client_id && (
            <p style={{...paragraphStyle, color: '#dc3545', backgroundColor: '#f8d7da', padding: '12px', borderRadius: '6px', border: '1px solid #f5c6cb'}}>
              ⚠️ Chatbot ID not found. Please ensure you're logged in and your account is properly set up.
            </p>
          )}
          
          <div style={codeBlockStyle}>
            {chatbotScript}
          </div>
          
          <button 
            style={copyButtonStyle}
            onClick={() => copyToClipboard(chatbotScript)}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
          >
            Copy Script
          </button>
        </div>

        {/* Implementation Guide */}
        <div style={cardStyle}>
          <h2 style={cardTitleStyle}>How to Embed Your Chatbot</h2>
          
          <div style={stepStyle}>
            <div>
              <span style={stepNumberStyle}>1</span>
              <strong>Copy the Script</strong>
            </div>
            <p style={paragraphStyle}>
              Click the "Copy Script" button above to copy the complete chatbot script to your clipboard.
            </p>
          </div>

          <div style={stepStyle}>
            <div>
              <span style={stepNumberStyle}>2</span>
              <strong>Add to Your Website</strong>
            </div>
            <p style={paragraphStyle}>
              Paste the script into your website's HTML, preferably just before the closing <code>&lt;/body&gt;</code> tag. 
              This ensures the chatbot loads after your page content.
            </p>
          </div>

          <div style={stepStyle}>
            <div>
              <span style={stepNumberStyle}>3</span>
              <strong>Customize the Greeting</strong>
            </div>
            <p style={paragraphStyle}>
              You can modify the <code>data-greeting</code> attribute to change the initial message that users see when they first interact with your chatbot.
            </p>
          </div>

          <div style={stepStyle}>
            <div>
              <span style={stepNumberStyle}>4</span>
              <strong>Test Your Integration</strong>
            </div>
            <p style={paragraphStyle}>
              After adding the script, refresh your website and look for the chatbot widget in the bottom-right corner. 
              Click on it to test the conversation flow.
            </p>
          </div>
        </div>

        {/* Advanced Configuration */}
        <div style={cardStyle}>
          <h2 style={cardTitleStyle}>Advanced Configuration</h2>
          
          <p style={paragraphStyle}>
            The chatbot script supports several configuration options:
          </p>
          
          <ul style={listStyle}>
            <li style={listItemStyle}>
              <strong>data-client-id:</strong> Your unique chatbot identifier (automatically included)
            </li>
            <li style={listItemStyle}>
              <strong>data-greeting:</strong> The initial message shown to users
            </li>
            <li style={listItemStyle}>
              <strong>data-position:</strong> Widget position (bottom-right, bottom-left, etc.)
            </li>
            <li style={listItemStyle}>
              <strong>data-theme:</strong> Color scheme (light, dark, custom)
            </li>
          </ul>

          <p style={paragraphStyle}>
            For more advanced customization options, contact our support team or refer to the 
            complete API documentation.
          </p>
        </div>

        {/* Troubleshooting */}
        <div style={cardStyle}>
          <h2 style={cardTitleStyle}>Troubleshooting</h2>
          
          <p style={paragraphStyle}>
            If you encounter any issues with the chatbot integration:
          </p>
          
          <ul style={listStyle}>
            <li style={listItemStyle}>
              Ensure the script is placed before the closing <code>&lt;/body&gt;</code> tag
            </li>
            <li style={listItemStyle}>
              Check that your client ID is correct and active
            </li>
            <li style={listItemStyle}>
              Verify that your website allows external scripts
            </li>
            <li style={listItemStyle}>
              Clear your browser cache and refresh the page
            </li>
          </ul>

          <p style={paragraphStyle}>
            If problems persist, please contact our technical support team with your client ID 
            and a description of the issue.
          </p>
        </div>
      </div>

      {/* Sign Out Loader Overlay */}
      {isSigningOut && (
        <div style={loaderOverlayStyle}>
          <div style={loaderContainerStyle}>
            <div style={spinnerStyle}></div>
            <p style={loaderTextStyle}>Signing out...</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmbeddingGuide
